import { useState } from "react";
import { Download, ImageDown, Loader2, RefreshCw, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { generateTripPoster } from "../lib/tripPoster";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

const WA_GREEN = "#25D366";

export const PosterDialog = ({ trip, progress, shareUrl, canCreateShare = false }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blob, setBlob] = useState(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [url, setUrl] = useState(shareUrl || null);

  const ensureUrl = async () => {
    if (shareUrl) return shareUrl;
    if (trip.is_public === false) return null;
    if (trip.share_token) return `${window.location.origin}/share/${trip.share_token}`;
    if (canCreateShare) {
      try {
        const { data } = await api.post(`/trips/${trip.id}/share`);
        return `${window.location.origin}/share/${data.share_token}`;
      } catch {
        return null;
      }
    }
    return null;
  };

  const generate = async () => {
    setBusy(true);
    try {
      const u = await ensureUrl();
      setUrl(u);
      const b = await generateTripPoster(trip, { progress, shareUrl: u });
      setBlob(b);
      setDataUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(b);
      });
    } catch {
      toast.error("Poster maken mislukte — probeer het opnieuw");
    } finally {
      setBusy(false);
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v && !dataUrl && !busy) generate();
  };

  const fileName = `voltroute-${(trip.name || "trip").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}.png`;

  const shareText = () => {
    const o = (trip.origin?.name || "").split(",")[0];
    const d = (trip.destination?.name || "").split(",")[0];
    let t = `⚡ ${trip.name} — elektrische roadtrip van ${o} naar ${d} (${Math.round(trip.route?.distance_km || 0)} km)`;
    if (progress?.started && !progress.finished) {
      const day = Math.min((progress.days_done || 0) + 1, progress.days_total);
      t += `\nWe zitten op dag ${day} van ${progress.days_total} — al ${Math.round(progress.driven_km).toLocaleString("nl-BE")} km gereden! 🚗`;
    } else if (progress?.finished) {
      t += `\nReis voltooid — ${Math.round(progress.total_km).toLocaleString("nl-BE")} km elektrisch gereden! 🏁`;
    }
    if (url) t += `\nVolg de reis hier: ${url}`;
    return t;
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.canShare;

  const shareNative = async () => {
    const file = new File([blob], fileName, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: trip.name, text: shareText() });
      } catch { /* geannuleerd */ }
    } else {
      toast.info("Direct delen met afbeelding wordt hier niet ondersteund — download de poster en deel via WhatsApp");
    }
  };

  const shareWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText())}`, "_blank", "noopener");
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = fileName;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="poster-open-button" variant="outline" className="rounded-full active:scale-95 transition-[transform,background-color] duration-200" title="Maak een deelbare trip-poster">
          <ImageDown className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Poster</span>
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="poster-dialog" className="rounded-2xl max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Trip-poster delen</DialogTitle>
          <DialogDescription className="text-xs">
            Een deelbare afbeelding met jullie route en reisstats — perfect voor WhatsApp of een story.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center">
          {busy ? (
            <div data-testid="poster-loading" className="w-full aspect-[9/16] max-h-[52vh] rounded-2xl bg-[#0F172A] flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 text-[#52B788] animate-spin" />
              <p className="text-xs text-white/60">Poster wordt gemaakt…</p>
            </div>
          ) : dataUrl ? (
            <img data-testid="poster-preview-image" src={dataUrl} alt="Trip-poster" className="rounded-2xl shadow-xl max-h-[52vh] w-auto border border-border" />
          ) : (
            <div className="w-full aspect-[9/16] max-h-[52vh] rounded-2xl bg-secondary" />
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {canNativeShare && (
            <Button data-testid="poster-share-native" onClick={shareNative} disabled={busy || !blob}
              className="rounded-full bg-primary hover:bg-[#1B4332] col-span-2">
              <Share2 className="w-4 h-4 mr-2" /> Deel de poster
            </Button>
          )}
          <Button data-testid="poster-share-whatsapp" onClick={shareWhatsApp} disabled={busy}
            className="rounded-full text-white hover:opacity-90" style={{ backgroundColor: WA_GREEN }}>
            <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.668-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </Button>
          <Button data-testid="poster-download" onClick={download} disabled={busy || !dataUrl} variant="outline" className="rounded-full">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground">
            {canNativeShare ? "Tip: \"Deel de poster\" stuurt de afbeelding direct mee." : "Download de poster en voeg hem toe aan je WhatsApp-bericht."}
          </p>
          <button data-testid="poster-refresh" onClick={generate} disabled={busy} title="Poster vernieuwen"
            className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200 shrink-0">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
